import type { TransactionAgreement } from '@/modules/investment/features/investment/types';
import type {
  Company,
  Issue,
  OperatingSnapshot,
  Opportunity,
  PortfolioStatus,
  Report,
  Risk,
} from '@/modules/portfolio/domain/portfolio/types';
import { createAppRequest } from '@/services/request';
import type {
  AppointmentDetails,
  FollowUpDetails,
} from '@/shared/projectLifecycle/FormDetails';

export type CompanyDetail = Company & {
  organization?: Company['organization'];
  contacts?: Company['contacts'];
  baseline?: {
    projectId: string;
    thesis: string;
    keyRisk: string;
    judgement: string;
    decisionConditions: Array<{
      title: string;
      required: boolean;
      status: string;
      owner: string;
      dueDate: string;
    }>;
  };
};

export interface PortfolioValuationDto {
  uuid: string;
  companyUuid: string;
  investmentRelationUuid?: string | null;
  valuationDate: string;
  validUntil?: string | null;
  method: string;
  basis?: string | null;
  fairValueSource?: string | null;
  evidenceAttachmentUuid?: string | null;
  value: number;
  status: string;
  changeNote?: string | null;
  createdByMemberUuid?: string | null;
  effectiveByMemberUuid?: string | null;
  effectiveAt?: string | null;
}

/** 投后项目分红登记（进基金现金台账 sourceType=dividend，计入回款与待分配）。 */
export interface PortfolioDividendDto {
  factStatus: string;
  factVersion: number;
  recoveryType?: string;
  sourceUrl?: string;
  impact?: {
    costBefore?: string;
    costAfter?: string;
    received?: string;
    principal?: string;
    income?: string;
  };

  uuid: string;
  companyUuid: string;
  relationUuid?: string | null;
  fundUuid?: string | null;
  fundNameSnapshot?: string;
  amount: number;
  principalAmount?: number | null;
  declaredDate?: string | null;
  receivedDate: string;
  remark?: string | null;
  attachmentUuid?: string | null;
}

/** 投后事件（融资/并购/诉讼/股东变化/高管变化/知情权请求/其他）。 */
export interface PortfolioEventDto {
  uuid: string;
  companyUuid: string;
  eventType: string;
  title: string;
  occurredAt: string;
  description?: string | null;
  status: string;
  owner: string;
  attachmentUuid?: string | null;
  /** 是否用印（服务端布尔口径）。 */
  sealRequired?: boolean;
  sealUuid?: string | null;
  sealName?: string | null;
  sealCompanyId?: string | null;
  sealCompanyName?: string | null;
  sealCopies?: number | null;
  createdAt?: string;
}

/** 项目跟进记录（走访/电话/会议/邮件等；记录型台账）。 */
export interface PortfolioFollowUpDto {
  details?: FollowUpDetails;
  uuid: string;
  companyUuid: string;
  followType: string;
  followedAt: string;
  participants: string;
  content: string;
  conclusion: string;
  nextAction: string;
  nextActionDueOn: string;
  recorder: string;
  recorderMemberUuid?: string | null;
  attachmentUuid?: string | null;
  createdAt?: string;
}

/** 投后会议（与投后项目的经营沟通会/专题会/走访会；不含法定三会）。
 *  与公司治理的「三会」（GovernanceMeeting）是不同台账。 */
export interface PortfolioFollowUpMeetingDto {
  details?: { mode?: string; content?: string };
  uuid: string;
  companyUuid: string;
  meetingType: string;
  title: string;
  heldAt: string;
  location: string;
  participants: string;
  agenda?: string | null;
  resolution?: string | null;
  nextAction: string;
  nextActionDueOn: string;
  host: string;
  hostMemberUuid?: string | null;
  attachmentUuid?: string | null;
  createdAt?: string;
}

export interface PortfolioHoldingMovementDto {
  uuid: string;
  companyUuid: string;
  relationUuid: string;
  movementType: string;
  movementDate: string;
  effectiveDate?: string | null;
  holdingDelta: number;
  amountDelta: number;
  reason: string;
}

export interface PortfolioAmendmentDto {
  id: string;
  transactionId: string;
  type: string;
  title: string;
  summary?: string;
  signedAt?: string;
  owner?: string;
  status: string;
  affectsAmount?: boolean;
  affectsHolding?: boolean;
  amountAfter?: number | null;
  holdingAfter?: number | null;
  writebackCompleted?: boolean;
  fund?: string;
  sourceProjectUuid?: string | null;
}

export interface InvestmentRelationDto {
  id: string;
  companyId: string;
  fundId: string;
  fundUuid?: string;
  fundNameSnapshot?: string;
  round: string;
  amount: number;
  postValuation: number;
  holding: number;
  investedAt: string;
  boardSeat: string;
}

/** 投后三会（董事会 / 股东会 / 监事会）扁平记录。 */
export type GovernanceMeeting = {
  details?: {
    mode?: string;
    motions?: Array<{
      content: string;
      ourVote: string;
      major: boolean;
      result?: string;
      remark?: string;
    }>;
  };
  uuid: string;
  companyUuid: string;
  meetingType: string;
  heldAt: string;
  subject: string;
  resolution: string | null;
  status: string;
  owner: string;
  attachmentUuid: string | null;
  /** 会议记录附件（复用同一单据，两个位）。 */
  minutesAttachmentUuid?: string | null;
  voteResult: string;
  attendingMemberUuids: string[] | null;
  boardAppointmentUuid: string | null;
  attendeeName: string;
  relatedIssueUuid: string | null;
  relatedRiskUuid: string | null;
};

/** 财报三表结构化记录（人工录入或 Agent 解析后写入）。 */
export type FinancialStatement = {
  uuid: string;
  companyUuid: string;
  period: string;
  currency: string;
  revenue: number | null;
  netProfit: number | null;
  grossProfit: number | null;
  grossMargin: number | null;
  netMargin: number | null;
  revenueGrowthYoy: number | null;
  goodwill: number | null;
  cashAndCashEquivalents: number | null;
  currentAssets: number | null;
  currentLiabilities: number | null;
  currentRatio: number | null;
  debtToAssetRatio: number | null;
  cashFromSales: number | null;
  cashPaidForPurchases: number | null;
  totalAssets: number | null;
  totalLiabilities: number | null;
  equity: number | null;
  operatingCashFlow: number | null;
  investingCashFlow: number | null;
  financingCashFlow: number | null;
  source: string;
  reportedAt: string;
  attachmentUuid: string | null;
  remark: string;
  /** 台账事实状态：draft / confirmed；确认后抽取不可覆盖。 */
  factStatus?: string;
  factVersion?: number;
};

/** 同比 / 环比（只读计算，不落库）。 */
export type FinancialTrend = {
  period: string;
  previousPeriod: string | null;
  lastYearPeriod: string | null;
  revenue: { current: number | null; qoq: number | null; yoy: number | null };
  netProfit: { current: number | null; qoq: number | null; yoy: number | null };
  totalAssets: {
    current: number | null;
    qoq: number | null;
    yoy: number | null;
  };
  equity: { current: number | null; qoq: number | null; yoy: number | null };
  operatingCashFlow: {
    current: number | null;
    qoq: number | null;
    yoy: number | null;
  };
  investingCashFlow: {
    current: number | null;
    qoq: number | null;
    yoy: number | null;
  };
  financingCashFlow: {
    current: number | null;
    qoq: number | null;
    yoy: number | null;
  };
  limitation: string;
};

/** 估值穿透结果（只读计算）。 */
export type ValuationPenetration = {
  items: {
    companyUuid: string;
    fundUuid: string | null;
    fundName: string;
    round: string;
    companyValuation: number;
    valuationDate: string | null;
    holdingRatio: number;
    penetratedValue: number;
  }[];
  total: number;
  limitation: string;
};

export type RegistrationChange = {
  id: string;
  companyId: string;
  changeType: string;
  before: string;
  after: string;
  changeDate: string;
  status: string;
  attachmentUuid?: string;
};
export type ReportSubmission = {
  id: string;
  companyId: string;
  period: string;
  reportType: string;
  dueDate: string;
  submittedAt?: string;
  status: string;
  reminderCount: number;
  attachmentUuid?: string /** 读时计算：截止日已过且未提交 */;
  overdue?: boolean;
};
export type BoardAppointment = {
  details?: AppointmentDetails;
  id: string;
  companyId: string;
  appointingParty: string;
  personName: string;
  personMemberUuid?: string | null;
  position: string;
  appointedAt: string;
  expiryAt?: string;
  status: string;
  /** 委派其他附件（`inv_board_appointment.other_attachment_uuid`）。 */
  otherAttachmentUuid?: string;
  /** 委派会议记录附件（`inv_board_appointment.minutes_attachment_uuid`）。 */
  minutesAttachmentUuid?: string;
};

/** 基金到投资项目层的现金流表现；不代表 LP 净 DPI/TVPI。 */
export type FundPerformanceRow = {
  fundUuid: string;
  fundName: string;
  invested: number;
  dividends: number;
  exitProceeds: number;
  recovered: number;
  unrealized: number;
  cashRecoveryRatio: number;
  portfolioValueMultiple: number;
  investmentLevelIrr: number | null;
  hasCashFlow: boolean;
};
export type PortfolioOverview = {
  activeCount: number;
  exitedCount: number;
  invested: number;
  dividends: number;
  exitProceeds: number;
  recovered: number;
  cashRecoveryRatio: number;
  portfolioValueMultiple: number;
  performanceScope: 'fund-to-investment';
  averageHoldingDays: number;
  valuationAsOf?: string;
  performanceNote?: string;
  funds?: FundPerformanceRow[];
};

export interface PortfolioInvestorRightDto {
  id: string;
  companyId: string;
  investmentRelationId?: string;
  sourceType: 'investor_right';
  sourceId?: string;
  rightType: string;
  title: string;
  content: string;
  obligationParty: string;
  reminderAt?: string;
  status: string;
  allowedNextStatuses: string[];
  executionNote: string;
  lastCheckedAt?: string;
  ownerMemberUuid?: string;
  attachmentUuid?: string;
  updatedAt: string;
}

export type CreatePortfolioInvestorRightInput = {
  investmentRelationUuid: string;
  rightType: string;
  title: string;
  content?: string;
  obligationParty?: string;
  reminderAt?: string;
  ownerMemberUuid?: string;
  attachmentUuid?: string;
};

export type UpdatePortfolioInvestorRightInput =
  Partial<CreatePortfolioInvestorRightInput>;

/** 投后条款台账。 */
export interface PortfolioClauseDto {
  id: string;
  companyId: string;
  clauseType: string;
  title: string;
  content: string;
  obligationParty: string;
  reminderAt?: string;
  status: string;
  allowedNextStatuses: string[];
  executionNote: string;
  lastCheckedAt?: string;
  ownerMemberUuid?: string;
  attachmentUuid?: string;
  updatedAt?: string;
}

/** 投后用印管理。 */
export interface PortfolioSealUseDto {
  sealUuid?: string | null;
  id: string;
  companyId: string;
  subject: string;
  sealName: string;
  sealOwner: string;
  documentName: string;
  counterparty: string;
  copies: number;
  purpose: string;
  applicant: string;
  appliedAt: string;
  usedAt?: string;
  status: string;
  allowedNextStatuses: string[];
  remark: string;
  updatedAt?: string;
}

/** 投后后续融资。 */
export interface PortfolioFinancingRoundDto {
  postMoneyValuation?: number;
  factStatus: string;
  factVersion: number;
  impact?: {
    basis?: Array<{
      relationUuid: string;
      holding: number;
      fundName?: string;
      round?: string;
    }>;
  };

  id: string;
  companyId: string;
  round: string;
  financingDate: string;
  preMoneyValuation: number;
  roundAmount: number;
  ourHoldingAfter?: number;
  ourInvestmentAmount: number;
  investors: Array<Record<string, unknown>>;
  agreementAttachmentUuid?: string;
  remark: string;
}

/** 投后项目资金流水。 */
export interface PortfolioCashFlowDto {
  readOnly?: boolean;
  sourceLabel?: string;
  id: string;
  companyId: string;
  direction: 'in' | 'out';
  category: string;
  amount: number;
  occurredAt: string;
  counterparty: string;
  applicant: string;
  appliedAt: string;
  remark: string;
}

/** 投后权益指标（只读）。 */
export interface PortfolioEquityMetrics {
  cashFlows?: PortfolioCashFlowDto[];
  companyId: string;
  currentCost: number;
  exitedCost: number;
  invested: number;
  recovered: number;
  unrealized: number;
  totalValue: number;
  holding: number;
  moc: number | null;
  irr: number | null;
  valuationId: string | null;
  valuationDate: string | null;
}

/**
 * 退出币种（契约《投后退出-接口契约-20261031》§0）。
 * 币种为人民币时「折算人民币」= 本币金额，前端禁用输入。
 */
export const PORTFOLIO_EXIT_CURRENCIES = [
  '人民币',
  '美元',
  '欧元',
  '港元',
  '其他',
] as const;

export type PortfolioExitCurrency = (typeof PORTFOLIO_EXIT_CURRENCIES)[number];

/** 退出单据状态口径（契约 §0，中文值直接落库）。 */
export const PORTFOLIO_EXIT_PLAN_STATUSES = [
  '草稿',
  '审批中',
  '审批通过',
  '已驳回',
] as const;

export const PORTFOLIO_EXIT_EXECUTION_STATUSES = [
  '草稿',
  '审批中',
  '审批通过',
  '已完成',
  '已驳回',
] as const;

/**
 * 历史 `decisionKind` 取值：仅用于**读**（兼容旧数据回显），
 * 前端不再发送该字段，也不再据此分段或做任何行为分支。
 */
export const PORTFOLIO_EXIT_DECISION_KINDS = ['普通', '需要执行'] as const;

export type PortfolioExitDecisionKind =
  (typeof PORTFOLIO_EXIT_DECISION_KINDS)[number];

/** 退出计划台账（契约 §1 / 表 `inv_portfolio_exit_plan`）。 */
export interface PortfolioExitPlanDto {
  uuid: string;
  companyUuid: string;
  /** 被投企业名称快照（列表展示用）。 */
  companyName?: string;
  /** 投资主体及轮次（用于带出投资金额与后续回写）。 */
  investmentRelationUuid?: string | null;
  /** 出资主体。 */
  investor: string;
  /** 投资金额（万元，按投资关系带出）。 */
  investmentAmount?: number | null;
  /** 预计退出日期 YYYY-MM-DD。 */
  expectedExitDate?: string | null;
  /** 拟退出方式（字典 exitMethods）。 */
  exitMethod: string;
  /** 退出时点估值（万元）。 */
  exitValuation?: number | null;
  /** 本次退出计划（≤2000）。 */
  planNote?: string | null;
  /** 退出风险及建议（≤2000）。 */
  riskNote?: string | null;
  /** 退出建议书（提交时必填）。 */
  proposalAttachmentUuid?: string | null;
  /** 退出协议，如有。 */
  agreementAttachmentUuid?: string | null;
  /** 草稿 / 审批中 / 审批通过 / 已驳回。 */
  status: string;
  submittedAt?: string | null;
  createdByMemberUuid?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type CreatePortfolioExitPlanInput = {
  companyUuid: string;
  investmentRelationUuid?: string | null;
  investor: string;
  investmentAmount?: number | null;
  expectedExitDate: string;
  exitMethod: string;
  exitValuation: number;
  planNote: string;
  riskNote: string;
  proposalAttachmentUuid?: string | null;
  agreementAttachmentUuid?: string | null;
  /** true = 保存并提交（服务端校验必填与退出建议书）。 */
  submit?: boolean;
};

export type UpdatePortfolioExitPlanInput = Partial<
  Omit<CreatePortfolioExitPlanInput, 'companyUuid'>
>;

/** 审批结果回填入参（契约 §1/§2/§3 的 approval-result 统一形状）。 */
export type PortfolioExitApprovalResultInput = {
  result: '通过' | '驳回';
  opinion?: string;
};

/** 投后退出决策（参考系统 F19「新增项目退出决策」口径 + 契约 §2 新增字段）。 */
export interface PortfolioExitDecisionDto {
  uuid: string;
  companyUuid: string;
  /** 企业名称快照。 */
  companyName?: string;
  /** 关联退出计划（契约 §2 新增）。 */
  planUuid?: string | null;
  /**
   * 决策口径（历史兼容列，**只读**）：后端已退化为纯兼容字段，新建一律「普通」。
   * 前端不发送、不据此分段、不做任何行为分支，仅原样回显历史数据。
   */
  decisionKind?: string | null;
  /** 预计回收金额（需要执行时必填，契约 §2 新增）。 */
  expectedRecoveryAmount?: number | null;
  /** 预计回收金额币种。 */
  expectedRecoveryCurrency?: string | null;
  /** 预计回收金额折算人民币。 */
  expectedRecoveryCny?: number | null;
  /** 原占股比（%）。 */
  originalHoldingRatio?: number | null;
  /** 出资主体对应投资关系（回写用）。 */
  investmentRelationUuid?: string | null;
  /** 出资主体。 */
  investor: string;
  /** 流程发起人。 */
  filler?: string;
  /** 经办部门。 */
  department?: string;
  /** 事项名称。 */
  itemTitle: string;
  /** 是否全部退出：全部退出 / 部分退出。 */
  fullExit: string;
  /** 退出方式。 */
  exitMethod: string;
  /** 退出日期 YYYY-MM-DD。 */
  exitDate?: string | null;
  /** 退出金额（万元）。 */
  exitAmount: number;
  /** 币种。 */
  currency?: string;
  /** 折算人民币（万元）；未填时后端回显原币值。 */
  convertedAmountCny?: number | null;
  /** 退出后持股比例（%）。 */
  holdingRatio?: number | null;
  /** 提请审议事项。 */
  reviewMatter?: string | null;
  /** 退出报告附件。 */
  reportAttachmentUuid?: string | null;
  /** 投资协议附件。 */
  agreementAttachmentUuid?: string | null;
  /** 其他附件。 */
  otherAttachmentUuid?: string | null;
  /** 事项结果：通过 / 未通过 / 暂缓（会后回填）。 */
  result?: string | null;
  /** 草稿 / 已决议。 */
  status: string;
  executedAt?: string | null;
  executedAmount?: number | null;
  executionNote?: string | null;
  executionResult?: string | null;
  executionAttachmentUuids?: string[] | null;
  completedAt?: string | null;
  completionChecklist?: string[] | null;
  completionMaterials?: string[] | null;
  deregistrationStatus?: string | null;
  deregistrationDetails?: string | null;
  liquidationDetails?: string | null;
  completionNote?: string | null;
  createdByMemberUuid?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CreatePortfolioExitDecisionInput = {
  companyUuid: string;
  investor: string;
  itemTitle: string;
  fullExit?: string;
  exitMethod: string;
  exitDate?: string;
  exitAmount: number;
  currency?: string;
  convertedAmountCny?: number;
  holdingRatio?: number;
  reviewMatter: string;
  reportAttachmentUuid: string;
  agreementAttachmentUuid: string;
  otherAttachmentUuid?: string;
  department?: string;
  /** 契约 §2 新增：关联退出计划。 */
  planUuid?: string | null;
  /**
   * 注意：**不发送 `decisionKind`**。后端已把它退化为纯兼容列（新建一律「普通」），
   * 有意不在提交白名单里出现，避免后来者误以为它还有语义。
   */
  /** 契约 §2 新增：预计回收金额（选填，不填不影响保存/提交）。 */
  expectedRecoveryAmount?: number | null;
  expectedRecoveryCurrency?: string | null;
  expectedRecoveryCny?: number | null;
  /** 契约 §2 新增：原占股比（%）。 */
  originalHoldingRatio?: number | null;
  investmentRelationUuid?: string | null;
  /** true = 保存并提交（服务端校验必填与报告/协议附件）。 */
  submit?: boolean;
};

export type UpdatePortfolioExitDecisionInput = Partial<
  Omit<CreatePortfolioExitDecisionInput, 'companyUuid'>
> & {
  result?: string;
  status?: string;
};

export type ExecutePortfolioExitDecisionInput = {
  executedAt: string;
  executedAmount: number;
  executionResult: string;
  executionNote?: string;
  executionAttachmentUuids?: string[];
};

export type CompletePortfolioExitDecisionInput = {
  completedAt: string;
  completionChecklist: string[];
  completionMaterials?: string[];
  deregistrationStatus: string;
  deregistrationDetails?: string;
  liquidationDetails?: string;
  completionNote?: string;
};

/**
 * 代持出资人明细（契约 §3 / 表 `inv_portfolio_exit_execution_investor`）。
 * `attribution`（出资人归属）只读带出，前端不猜取值。
 */
export interface PortfolioExitExecutionInvestorDto {
  uuid?: string;
  executionUuid?: string;
  investorName: string;
  attribution?: string | null;
  exitAmount?: number | null;
  exitCurrency?: string | null;
  exitAmountCny?: number | null;
  principalAmount?: number | null;
  principalCurrency?: string | null;
  principalCny?: number | null;
  sortOrder?: number | null;
}

/** 退出执行单（契约 §3 / 表 `inv_portfolio_exit_execution`）。 */
export interface PortfolioExitExecutionDto {
  uuid: string;
  companyUuid: string;
  companyName?: string;
  decisionUuid: string;
  investmentRelationUuid?: string | null;
  /** 是否代持：区分第 4/5 段。 */
  isNominee: boolean;
  applicant?: string | null;
  appliedAt?: string | null;
  /** 全部退出 / 部分退出（从决策带出）。 */
  fullExit: string;
  /** 从决策带出。 */
  exitMethod: string;
  expectedRecoveryAmount?: number | null;
  originalHoldingRatio?: number | null;
  /** 执行后占股比（%）；全部退出时必须为 0。 */
  holdingRatioAfter: number;
  exitAmount?: number | null;
  exitCurrency?: string | null;
  exitAmountCny?: number | null;
  principalAmount?: number | null;
  principalCurrency?: string | null;
  principalCny?: number | null;
  /** 执行结果（≤2000）。 */
  executionResult?: string | null;
  executedAt?: string | null;
  attachmentUuids?: string[] | null;
  /** 草稿 / 审批中 / 审批通过 / 已完成 / 已驳回。 */
  status: string;
  /** 代持出资人明细（详情接口返回）。 */
  investors?: PortfolioExitExecutionInvestorDto[] | null;
  completedAt?: string | null;
  completionMaterials?: string[] | null;
  completionChecklist?: string[] | null;
  deregistrationStatus?: string | null;
  deregistrationDetails?: string | null;
  liquidationDetails?: string | null;
  completionNote?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type CreatePortfolioExitExecutionInput = {
  companyUuid: string;
  decisionUuid: string;
  isNominee: boolean;
  investmentRelationUuid?: string | null;
  fullExit: string;
  exitMethod: string;
  expectedRecoveryAmount?: number | null;
  originalHoldingRatio?: number | null;
  holdingRatioAfter: number;
  exitAmount?: number | null;
  exitCurrency?: string | null;
  exitAmountCny?: number | null;
  principalAmount?: number | null;
  principalCurrency?: string | null;
  principalCny?: number | null;
  executionResult: string;
  executedAt?: string | null;
  attachmentUuids?: string[];
  /** 代持出资人明细（isNominee=true 时提交）。 */
  investors?: PortfolioExitExecutionInvestorDto[];
  /** true = 保存并提交。 */
  submit?: boolean;
};

export type UpdatePortfolioExitExecutionInput = Partial<
  Omit<CreatePortfolioExitExecutionInput, 'companyUuid'>
>;

/** 登记执行完成入参：`complete` 会触发持股/成本回写（契约 §3.1）。 */
export type CompletePortfolioExitExecutionInput = {
  executedAt?: string | null;
  executionResult?: string;
  completedAt?: string | null;
  completionChecklist?: string[];
  completionMaterials?: string[];
  deregistrationStatus?: string;
  deregistrationDetails?: string;
  liquidationDetails?: string;
  completionNote?: string;
};

export const portfolioApi = {
  listCompanies: () =>
    createAppRequest()<Company[]>('/bff/portfolio/companies'),
  getOverview: () =>
    createAppRequest()<PortfolioOverview>('/bff/portfolio/overview'),

  getCompany: (uuid: string) =>
    createAppRequest()<CompanyDetail>(`/bff/portfolio/companies/${uuid}`),

  updateCompanyStatus: (uuid: string, status: PortfolioStatus) =>
    createAppRequest()<Company>(`/bff/portfolio/companies/${uuid}/status`, {
      method: 'PATCH',
      data: { status },
    }),

  listCompanyAgreements: (companyUuid: string) =>
    createAppRequest()<
      Array<TransactionAgreement & { investmentRelationUuid?: string }>
    >(`/bff/portfolio/companies/${companyUuid}/agreements`),
  listRelations: () =>
    createAppRequest()<InvestmentRelationDto[]>('/bff/portfolio/relations'),

  listValuations: (companyUuid: string) =>
    createAppRequest()<PortfolioValuationDto[]>('/bff/portfolio/valuations', {
      params: { companyUuid },
    }),

  // ── 投后洞察：三会 / 财报三表 / 影子对标 / 估值穿透 ──
  listMeetings: (companyUuid: string) =>
    createAppRequest()<GovernanceMeeting[]>(
      `/bff/portfolio/companies/${companyUuid}/meetings`,
    ),

  createMeeting: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<GovernanceMeeting>(
      `/bff/portfolio/companies/${companyUuid}/meetings`,
      {
        method: 'POST',
        data,
      },
    ),

  updateMeeting: (
    companyUuid: string,
    meetingUuid: string,
    data: Record<string, unknown>,
  ) =>
    createAppRequest()<GovernanceMeeting>(
      `/bff/portfolio/companies/${companyUuid}/meetings/${meetingUuid}`,
      { method: 'PATCH', data },
    ),

  createIssueFromMeeting: (
    companyUuid: string,
    meetingUuid: string,
    data?: {
      title?: string;
      type?: string;
      importance?: string;
      riskLevel?: string;
    },
  ) =>
    createAppRequest()<unknown>(
      `/bff/portfolio/companies/${companyUuid}/meetings/${meetingUuid}/issue`,
      { method: 'POST', data: data ?? {} },
    ),

  createRiskFromMeeting: (
    companyUuid: string,
    meetingUuid: string,
    data?: { title?: string; type?: string; level?: string },
  ) =>
    createAppRequest()<unknown>(
      `/bff/portfolio/companies/${companyUuid}/meetings/${meetingUuid}/risk`,
      { method: 'POST', data: data ?? {} },
    ),

  // ── 投后台账：条款 / 用印 / 后续融资 / 资金流水 / 权益指标 ──

  listClauses: (companyUuid: string, status?: string) =>
    createAppRequest()<PortfolioClauseDto[]>(
      `/bff/portfolio/companies/${companyUuid}/clauses`,
      { params: status ? { status } : undefined },
    ),

  createClause: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioClauseDto>(
      `/bff/portfolio/companies/${companyUuid}/clauses`,
      { method: 'POST', data },
    ),

  listInvestorRights: (companyUuid: string, investmentRelationUuid?: string) =>
    createAppRequest()<PortfolioInvestorRightDto[]>(
      `/bff/portfolio/companies/${companyUuid}/investor-rights`,
      {
        params: investmentRelationUuid ? { investmentRelationUuid } : undefined,
      },
    ),

  createInvestorRight: (
    companyUuid: string,
    data: CreatePortfolioInvestorRightInput,
  ) =>
    createAppRequest()<PortfolioInvestorRightDto>(
      `/bff/portfolio/companies/${companyUuid}/investor-rights`,
      { method: 'POST', data },
    ),

  updateInvestorRight: (
    uuid: string,
    data: UpdatePortfolioInvestorRightInput,
  ) =>
    createAppRequest()<PortfolioInvestorRightDto>(
      `/bff/portfolio/investor-rights/${uuid}`,
      { method: 'PATCH', data },
    ),

  updateInvestorRightStatus: (
    uuid: string,
    data: { status: string; executionNote?: string; lastCheckedAt?: string },
  ) =>
    createAppRequest()<PortfolioInvestorRightDto>(
      `/bff/portfolio/investor-rights/${uuid}/status`,
      { method: 'POST', data },
    ),

  updateClauseStatus: (
    uuid: string,
    data: { status: string; executionNote?: string; lastCheckedAt?: string },
  ) =>
    createAppRequest()<PortfolioClauseDto>(
      `/bff/portfolio/clauses/${uuid}/status`,
      {
        method: 'POST',
        data,
      },
    ),

  updateClause: (uuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioClauseDto>(`/bff/portfolio/clauses/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listClauseReminders: (companyUuid: string, withinDays = 30) =>
    createAppRequest()<PortfolioClauseDto[]>(
      `/bff/portfolio/companies/${companyUuid}/clauses/reminders`,
      { params: { withinDays } },
    ),

  listSealUses: (companyUuid: string, status?: string) =>
    createAppRequest()<PortfolioSealUseDto[]>(
      `/bff/portfolio/companies/${companyUuid}/seals`,
      { params: status ? { status } : undefined },
    ),

  createSealUse: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioSealUseDto>(
      `/bff/portfolio/companies/${companyUuid}/seals`,
      { method: 'POST', data },
    ),

  updateSealUseStatus: (
    companyUuid: string,
    sealUuid: string,
    data: {
      status: string;
      usedAt?: string;
      remark?: string;
      /** 状态改「已用印」时一并送盖章回传件最顺手。 */
      sealedAttachmentUuid?: string;
    },
  ) =>
    createAppRequest()<PortfolioSealUseDto>(
      `/bff/portfolio/companies/${companyUuid}/seals/${sealUuid}/status`,
      { method: 'POST', data },
    ),

  updateSealUse: (
    companyUuid: string,
    sealUuid: string,
    data: Record<string, unknown>,
  ) =>
    createAppRequest()<PortfolioSealUseDto>(
      `/bff/portfolio/companies/${companyUuid}/seals/${sealUuid}`,
      { method: 'PATCH', data },
    ),

  listRecoveries: (companyUuid: string, investmentRelationUuid?: string) =>
    createAppRequest()<PortfolioDividendDto[]>('/bff/portfolio/recoveries', {
      params: { companyUuid, investmentRelationUuid },
    }),
  createRecovery: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioDividendDto>('/bff/portfolio/recoveries', {
      method: 'POST',
      data: { ...data, companyUuid },
    }),
  updateRecovery: (uuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioDividendDto>(
      `/bff/portfolio/recoveries/${uuid}`,
      { method: 'PATCH', data },
    ),
  confirmRecovery: (uuid: string, expectedVersion: number) =>
    createAppRequest()<PortfolioDividendDto>(
      `/bff/portfolio/recoveries/${uuid}/confirm`,
      { method: 'POST', data: { expectedVersion } },
    ),
  reverseRecovery: (uuid: string, expectedVersion: number, reason: string) =>
    createAppRequest()<PortfolioDividendDto>(
      `/bff/portfolio/recoveries/${uuid}/reverse`,
      { method: 'POST', data: { expectedVersion, reason } },
    ),
  confirmFinancingRound: (
    companyUuid: string,
    uuid: string,
    expectedVersion: number,
  ) =>
    createAppRequest()<PortfolioFinancingRoundDto>(
      `/bff/portfolio/companies/${companyUuid}/financing-rounds/${uuid}/confirm`,
      { method: 'POST', data: { expectedVersion } },
    ),

  listFinancingRounds: (companyUuid: string) =>
    createAppRequest()<PortfolioFinancingRoundDto[]>(
      `/bff/portfolio/companies/${companyUuid}/financing-rounds`,
    ),

  createFinancingRound: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioFinancingRoundDto>(
      `/bff/portfolio/companies/${companyUuid}/financing-rounds`,
      { method: 'POST', data },
    ),

  updateFinancingRound: (
    companyUuid: string,
    roundUuid: string,
    data: Record<string, unknown>,
  ) =>
    createAppRequest()<PortfolioFinancingRoundDto>(
      `/bff/portfolio/companies/${companyUuid}/financing-rounds/${roundUuid}`,
      { method: 'PATCH', data },
    ),

  listCashFlows: (
    companyUuid: string,
    direction?: string,
    investmentRelationUuid?: string,
  ) =>
    createAppRequest()<PortfolioCashFlowDto[]>(
      `/bff/portfolio/companies/${companyUuid}/cash-flows`,
      { params: { direction, investmentRelationUuid } },
    ),

  createCashFlow: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioCashFlowDto>(
      `/bff/portfolio/companies/${companyUuid}/cash-flows`,
      { method: 'POST', data },
    ),

  updateCashFlow: (
    companyUuid: string,
    flowUuid: string,
    data: Record<string, unknown>,
  ) =>
    createAppRequest()<PortfolioCashFlowDto>(
      `/bff/portfolio/companies/${companyUuid}/cash-flows/${flowUuid}`,
      { method: 'PATCH', data },
    ),

  getEquityMetrics: (companyUuid: string, investmentRelationUuid?: string) =>
    createAppRequest()<PortfolioEquityMetrics>(
      `/bff/portfolio/companies/${companyUuid}/equity-metrics`,
      { params: { investmentRelationUuid } },
    ),

  listStatements: (companyUuid: string) =>
    createAppRequest()<FinancialStatement[]>(
      `/bff/portfolio/companies/${companyUuid}/statements`,
    ),

  createStatement: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<FinancialStatement>(
      `/bff/portfolio/companies/${companyUuid}/statements`,
      {
        method: 'POST',
        data,
      },
    ),

  confirmStatement: (statementUuid: string) =>
    createAppRequest()<FinancialStatement>(
      `/bff/portfolio/statements/${statementUuid}/confirm`,
      { method: 'POST' },
    ),

  statementTrend: (companyUuid: string, period: string) =>
    createAppRequest()<FinancialTrend>(
      `/bff/portfolio/companies/${companyUuid}/statements/trend`,
      {
        params: { period },
      },
    ),

  valuationPenetration: (companyUuid?: string) =>
    createAppRequest()<ValuationPenetration>(
      '/bff/portfolio/valuation-penetration',
      {
        params: companyUuid ? { companyUuid } : undefined,
      },
    ),

  listRegistrationChanges: (companyUuid: string) =>
    createAppRequest()<RegistrationChange[]>(
      '/bff/portfolio/registration-changes',
      { params: { companyUuid } },
    ),
  createRegistrationChange: (data: Record<string, unknown>) =>
    createAppRequest()<RegistrationChange>(
      '/bff/portfolio/registration-changes',
      { method: 'POST', data },
    ),
  updateRegistrationChange: (id: string, data: Record<string, unknown>) =>
    createAppRequest()<RegistrationChange>(
      `/bff/portfolio/registration-changes/${id}`,
      { method: 'PATCH', data },
    ),
  listReportSubmissions: (companyUuid: string) =>
    createAppRequest()<ReportSubmission[]>(
      '/bff/portfolio/report-submissions',
      { params: { companyUuid } },
    ),
  createReportSubmission: (data: Record<string, unknown>) =>
    createAppRequest()<ReportSubmission>('/bff/portfolio/report-submissions', {
      method: 'POST',
      data,
    }),
  updateReportSubmission: (id: string, data: Record<string, unknown>) =>
    createAppRequest()<ReportSubmission>(
      `/bff/portfolio/report-submissions/${id}`,
      { method: 'PATCH', data },
    ),
  listBoardAppointments: (companyUuid: string) =>
    createAppRequest()<BoardAppointment[]>(
      '/bff/portfolio/board-appointments',
      { params: { companyUuid } },
    ),
  createBoardAppointment: (data: Record<string, unknown>) =>
    createAppRequest()<BoardAppointment>('/bff/portfolio/board-appointments', {
      method: 'POST',
      data,
    }),
  updateBoardAppointment: (id: string, data: Record<string, unknown>) =>
    createAppRequest()<BoardAppointment>(
      `/bff/portfolio/board-appointments/${id}`,
      { method: 'PATCH', data },
    ),

  createValuation: (
    data: Omit<
      PortfolioValuationDto,
      | 'uuid'
      | 'status'
      | 'createdByMemberUuid'
      | 'effectiveByMemberUuid'
      | 'effectiveAt'
    >,
  ) =>
    createAppRequest()<PortfolioValuationDto>('/bff/portfolio/valuations', {
      method: 'POST',
      data,
    }),

  makeValuationEffective: (uuid: string) =>
    createAppRequest()<PortfolioValuationDto>(
      `/bff/portfolio/valuations/${uuid}/effective`,
      {
        method: 'POST',
      },
    ),

  voidValuation: (uuid: string, reason: string) =>
    createAppRequest()<PortfolioValuationDto>(
      `/bff/portfolio/valuations/${uuid}/void`,
      { method: 'POST', data: { reason } },
    ),

  updateDividend: (uuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioDividendDto>(
      `/bff/portfolio/dividends/${uuid}`,
      {
        method: 'PATCH',
        data,
      },
    ),

  voidDividend: (uuid: string, reason: string) =>
    createAppRequest()<PortfolioDividendDto>(
      `/bff/portfolio/dividends/${uuid}/void`,
      { method: 'POST', data: { reason } },
    ),

  listHoldingMovements: (relationUuid: string) =>
    createAppRequest()<PortfolioHoldingMovementDto[]>(
      `/bff/portfolio/holding-movements/${relationUuid}`,
    ),

  listCompanyHoldingMovements: (companyUuid: string) =>
    createAppRequest()<PortfolioHoldingMovementDto[]>(
      `/bff/portfolio/companies/${companyUuid}/holding-movements`,
    ),

  listCompanyAmendments: (companyUuid: string) =>
    createAppRequest()<PortfolioAmendmentDto[]>(
      `/bff/portfolio/companies/${companyUuid}/amendments`,
    ),

  listCompanyDocuments: (companyUuid: string) =>
    createAppRequest()<
      Array<{
        uuid: string;
        fileName: string;
        docType?: string;
        bizType: string;
        bizUuid: string;
        stageCategory: string;
        sourceLabel: string;
        remark?: string;
        sizeBytes?: number;
        createdAt?: string;
      }>
    >(`/bff/portfolio/companies/${companyUuid}/documents`),

  listDocumentCenter: (projectUuid: string) =>
    createAppRequest()<
      Array<{
        uuid: string;
        fileName: string;
        docType?: string;
        bizType: string;
        bizUuid: string;
        stageCategory: string;
        sourceLabel: string;
        remark?: string;
        sizeBytes?: number;
        createdAt?: string;
      }>
    >('/bff/investment/document-center', {
      method: 'GET',
      params: { projectUuid },
    }),

  createHoldingMovement: (data: Omit<PortfolioHoldingMovementDto, 'uuid'>) =>
    createAppRequest()<PortfolioHoldingMovementDto>(
      '/bff/portfolio/holding-movements',
      { method: 'POST', data },
    ),

  listOpportunities: () =>
    createAppRequest()<Opportunity[]>('/bff/portfolio/opportunities'),

  createOpportunity: (
    companyUuid: string,
    kind: Opportunity['kind'],
    details?: {
      signal?: string;
      recommendedMethod?: string;
      priority?: string;
      expectedExitAmount?: number;
      timeWindow?: string;
      judgmentConclusion?: string;
      suggestedAmount?: number;
      suggestedRound?: string;
      rationale?: string;
    },
  ) =>
    createAppRequest()<Opportunity>('/bff/portfolio/opportunities', {
      method: 'POST',
      data: { companyUuid, kind, ...details },
    }),

  listSnapshots: (companyUuid?: string) =>
    createAppRequest()<OperatingSnapshot[]>('/bff/portfolio/snapshots', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createSnapshot: (
    data: Omit<OperatingSnapshot, 'id' | 'updatedAt'> & { companyId: string },
  ) =>
    createAppRequest()<OperatingSnapshot>('/bff/portfolio/snapshots', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        period: data.period,
        revenue: data.revenue,
        budgetRevenue: data.budgetRevenue,
        grossMargin: data.grossMargin,
        cashFlow: data.cashFlow,
        cashBalance: data.cashBalance,
        orders: data.orders,
        rndRatio: data.rndRatio,
        budgetVariance: data.budgetVariance,
        anomalies: data.anomalies,
      },
    }),

  updateSnapshot: (uuid: string, data: Partial<OperatingSnapshot>) =>
    createAppRequest()<OperatingSnapshot>(`/bff/portfolio/snapshots/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listIssues: (companyUuid?: string) =>
    createAppRequest()<Issue[]>('/bff/portfolio/issues', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createIssue: (
    data: Omit<Issue, 'id' | 'createdAt'> & { companyId: string },
  ) =>
    createAppRequest()<Issue>('/bff/portfolio/issues', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        details: data.details,
        title: data.title,
        type: data.type,
        importance: data.importance,
        riskLevel: data.riskLevel,
        status: data.status,
        owner: data.owner,
        ownerMemberUuid: data.ownerMemberUuid,
        deadline: data.deadline,
        nextAction: data.nextAction,
        summary: data.summary,
        // 创建弹窗即可上传的附件。
        attachmentUuid: data.attachmentUuid,
      },
    }),

  updateIssue: (uuid: string, data: Partial<Issue>) =>
    createAppRequest()<Issue>(`/bff/portfolio/issues/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listRisks: (companyUuid?: string) =>
    createAppRequest()<Risk[]>('/bff/portfolio/risks', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createRisk: (data: Omit<Risk, 'id' | 'createdAt'> & { companyId: string }) =>
    createAppRequest()<Risk>('/bff/portfolio/risks', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        issueUuid: data.issueId,
        title: data.title,
        details: data.details,
        type: data.type,
        level: data.level,
        status: data.status,
        owner: data.owner,
        ownerMemberUuid: data.ownerMemberUuid,
        deadline: data.deadline,
        response: data.response,
        // 风险附件 + 会议记录附件（inv_portfolio_risk 两列）。
        attachmentUuid: data.attachmentUuid,
        minutesAttachmentUuid: data.minutesAttachmentUuid,
      },
    }),

  notifyRisk: (uuid: string) =>
    createAppRequest()(`/bff/portfolio/risks/${uuid}/notify`, {
      method: 'POST',
    }),
  updateRisk: (uuid: string, data: Partial<Risk>) =>
    createAppRequest()<Risk>(`/bff/portfolio/risks/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listReports: (companyUuid?: string) =>
    createAppRequest()<Report[]>('/bff/portfolio/reports', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createReport: (data: {
    companyId: string;
    name: string;
    type?: Report['type'];
    period?: string;
    owner?: string;
    deadline?: string;
    judgement?: string;
    /** 修订版本：被更正的已发布报告 UUID。 */
    correctsReportId?: string;
    correctionReason?: string;
    /** 报告附件 uuid（先上传再随报告提交）。 */
    attachmentUuid?: string;
    /** 版本属性；后端只认「终版」「历年」，其余 400。 */
    versionKind?: '终版' | '历年';
  }) =>
    createAppRequest()<Report>('/bff/portfolio/reports', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        name: data.name,
        type: data.type,
        period: data.period,
        owner: data.owner,
        deadline: data.deadline,
        judgement: data.judgement,
        correctsReportUuid: data.correctsReportId,
        correctionReason: data.correctionReason,
        // 创建时即可上传报告附件并标版本属性。
        attachmentUuid: data.attachmentUuid,
        versionKind: data.versionKind,
      },
    }),

  /** 分红登记（需 canGovernanceEdit）；挂基金时同步入账基金现金台账。 */
  createDividend: (
    companyUuid: string,
    data: {
      relationUuid?: string;
      amount: number;
      receivedDate: string;
      declaredDate?: string;
      remark?: string;
      attachmentUuid?: string;
    },
  ) =>
    createAppRequest()<PortfolioDividendDto>('/bff/portfolio/dividends', {
      method: 'POST',
      data: { companyUuid, ...data },
    }),

  listDividends: (companyUuid: string) =>
    createAppRequest()<PortfolioDividendDto[]>('/bff/portfolio/dividends', {
      params: { companyUuid },
    }),

  /** 投后事件登记（记录型；工作台自动生成待办）。 */
  createEvent: (
    companyUuid: string,
    data: {
      title: string;
      eventType: string;
      occurredAt?: string;
      description?: string;
      owner?: string;
      attachmentUuid?: string;
      /** 是否用印；为 true 时需同时提交用印公司、印章与份数。 */
      sealRequired?: boolean;
      sealCompanyId?: string;
      sealUuid?: string;
      sealCopies?: number;
    },
  ) =>
    createAppRequest()<PortfolioEventDto>('/bff/portfolio/events', {
      method: 'POST',
      data: { companyUuid, ...data },
    }),

  listEvents: (companyUuid: string) =>
    createAppRequest()<PortfolioEventDto[]>('/bff/portfolio/events', {
      params: { companyUuid },
    }),

  updateEventStatus: (uuid: string, status: string) =>
    createAppRequest()<PortfolioEventDto>(`/bff/portfolio/events/${uuid}`, {
      method: 'PATCH',
      data: { status },
    }),
  updateEvent: (uuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioEventDto>(`/bff/portfolio/events/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  /** 项目跟进台账（走访/电话/会议/邮件）。 */
  listFollowUps: (companyUuid: string) =>
    createAppRequest()<PortfolioFollowUpDto[]>('/bff/portfolio/follow-ups', {
      params: { companyUuid },
    }),

  createFollowUp: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioFollowUpDto>('/bff/portfolio/follow-ups', {
      method: 'POST',
      data: { companyUuid, ...data },
    }),

  updateFollowUp: (uuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioFollowUpDto>(
      `/bff/portfolio/follow-ups/${uuid}`,
      { method: 'PATCH', data },
    ),

  /** 投后会议台账（经营沟通会/专题会/走访会）。 */
  listFollowUpMeetings: (companyUuid: string) =>
    createAppRequest()<PortfolioFollowUpMeetingDto[]>(
      '/bff/portfolio/meetings',
      { params: { companyUuid } },
    ),

  createFollowUpMeeting: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioFollowUpMeetingDto>('/bff/portfolio/meetings', {
      method: 'POST',
      data: { companyUuid, ...data },
    }),

  updateFollowUpMeeting: (uuid: string, data: Record<string, unknown>) =>
    createAppRequest()<PortfolioFollowUpMeetingDto>(
      `/bff/portfolio/meetings/${uuid}`,
      { method: 'PATCH', data },
    ),

  // ── 退出计划台账（契约 §1 `/bff/portfolio/exit-plans`）──
  listExitPlans: (companyUuid: string) =>
    createAppRequest()<PortfolioExitPlanDto[]>('/bff/portfolio/exit-plans', {
      params: { companyUuid },
    }),
  getExitPlan: (uuid: string) =>
    createAppRequest()<PortfolioExitPlanDto>(
      `/bff/portfolio/exit-plans/${uuid}`,
    ),
  createExitPlan: (data: CreatePortfolioExitPlanInput) =>
    createAppRequest()<PortfolioExitPlanDto>('/bff/portfolio/exit-plans', {
      method: 'POST',
      data,
    }),
  updateExitPlan: (uuid: string, data: UpdatePortfolioExitPlanInput) =>
    createAppRequest()<PortfolioExitPlanDto>(
      `/bff/portfolio/exit-plans/${uuid}`,
      { method: 'PATCH', data },
    ),
  submitExitPlan: (uuid: string) =>
    createAppRequest()<PortfolioExitPlanDto>(
      `/bff/portfolio/exit-plans/${uuid}/submit`,
      { method: 'POST' },
    ),
  recordExitPlanApprovalResult: (
    uuid: string,
    data: PortfolioExitApprovalResultInput,
  ) =>
    createAppRequest()<PortfolioExitPlanDto>(
      `/bff/portfolio/exit-plans/${uuid}/approval-result`,
      { method: 'POST', data },
    ),
  deleteExitPlan: (uuid: string) =>
    createAppRequest()<{ uuid: string; deleted: boolean }>(
      `/bff/portfolio/exit-plans/${uuid}`,
      { method: 'DELETE' },
    ),

  // ── 退出决策台账（参考系统 F19「新增项目退出决策」）──
  /**
   * 决策列表：一次取全量。
   * 口径不再分段 → 不再透传 `decisionKind` 过滤（后端该列已退化为纯兼容字段）。
   */
  listExitDecisions: (companyUuid?: string) =>
    createAppRequest()<PortfolioExitDecisionDto[]>(
      '/bff/portfolio/exit-decisions',
      {
        params: companyUuid ? { companyUuid } : undefined,
      },
    ),
  getExitDecision: (uuid: string) =>
    createAppRequest()<PortfolioExitDecisionDto>(
      `/bff/portfolio/exit-decisions/${uuid}`,
    ),
  createExitDecision: (data: CreatePortfolioExitDecisionInput) =>
    createAppRequest()<PortfolioExitDecisionDto>(
      '/bff/portfolio/exit-decisions',
      {
        method: 'POST',
        data,
      },
    ),
  updateExitDecision: (uuid: string, data: UpdatePortfolioExitDecisionInput) =>
    createAppRequest()<PortfolioExitDecisionDto>(
      `/bff/portfolio/exit-decisions/${uuid}`,
      { method: 'PATCH', data },
    ),
  deleteExitDecision: (uuid: string) =>
    createAppRequest()<{ uuid: string; deleted: boolean }>(
      `/bff/portfolio/exit-decisions/${uuid}`,
      { method: 'DELETE' },
    ),

  completeExitDecision: (
    uuid: string,
    data: CompletePortfolioExitDecisionInput,
  ) =>
    createAppRequest()<PortfolioExitDecisionDto>(
      `/bff/portfolio/exit-decisions/${uuid}/complete`,
      { method: 'POST', data },
    ),
  executeExitDecision: (
    uuid: string,
    data: ExecutePortfolioExitDecisionInput,
  ) =>
    createAppRequest()<PortfolioExitDecisionDto>(
      `/bff/portfolio/exit-decisions/${uuid}/execute`,
      { method: 'POST', data },
    ),
  /** 契约 §2 新增：提交决策进审批 → 状态 审批中。 */
  submitExitDecision: (uuid: string) =>
    createAppRequest()<PortfolioExitDecisionDto>(
      `/bff/portfolio/exit-decisions/${uuid}/submit`,
      { method: 'POST' },
    ),
  /** 契约 §2 新增：回填审批结果 → 已决议 / 已驳回。 */
  recordExitDecisionApprovalResult: (
    uuid: string,
    data: PortfolioExitApprovalResultInput,
  ) =>
    createAppRequest()<PortfolioExitDecisionDto>(
      `/bff/portfolio/exit-decisions/${uuid}/approval-result`,
      { method: 'POST', data },
    ),

  // ── 退出执行台账（契约 §3 `/bff/portfolio/exit-executions`）──
  /**
   * 一次取全量执行单：`isNominee` 只在调用方确实要按口径筛选时才传
   * （「退出执行」段已合并代持/非代持，默认不传 → 后端返回全部）。
   */
  listExitExecutions: (companyUuid: string, isNominee?: boolean) =>
    createAppRequest()<PortfolioExitExecutionDto[]>(
      '/bff/portfolio/exit-executions',
      {
        params:
          isNominee === undefined
            ? { companyUuid }
            : { companyUuid, isNominee: String(isNominee) },
      },
    ),
  getExitExecution: (uuid: string) =>
    createAppRequest()<PortfolioExitExecutionDto>(
      `/bff/portfolio/exit-executions/${uuid}`,
    ),
  createExitExecution: (data: CreatePortfolioExitExecutionInput) =>
    createAppRequest()<PortfolioExitExecutionDto>(
      '/bff/portfolio/exit-executions',
      { method: 'POST', data },
    ),
  updateExitExecution: (
    uuid: string,
    data: UpdatePortfolioExitExecutionInput,
  ) =>
    createAppRequest()<PortfolioExitExecutionDto>(
      `/bff/portfolio/exit-executions/${uuid}`,
      { method: 'PATCH', data },
    ),
  submitExitExecution: (uuid: string) =>
    createAppRequest()<PortfolioExitExecutionDto>(
      `/bff/portfolio/exit-executions/${uuid}/submit`,
      { method: 'POST' },
    ),
  recordExitExecutionApprovalResult: (
    uuid: string,
    data: PortfolioExitApprovalResultInput,
  ) =>
    createAppRequest()<PortfolioExitExecutionDto>(
      `/bff/portfolio/exit-executions/${uuid}/approval-result`,
      { method: 'POST', data },
    ),
  /** 登记执行完成：状态 → 已完成，并触发持股/成本回写（契约 §3.1）。 */
  completeExitExecution: (
    uuid: string,
    data: CompletePortfolioExitExecutionInput,
  ) =>
    createAppRequest()<PortfolioExitExecutionDto>(
      `/bff/portfolio/exit-executions/${uuid}/complete`,
      { method: 'POST', data },
    ),
  deleteExitExecution: (uuid: string) =>
    createAppRequest()<{ uuid: string; deleted: boolean }>(
      `/bff/portfolio/exit-executions/${uuid}`,
      { method: 'DELETE' },
    ),
  updateReport: (uuid: string, data: Partial<Report>) =>
    createAppRequest()<Report>(`/bff/portfolio/reports/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listShareholders: (companyUuid: string, onlyCurrent = true) =>
    createAppRequest()<CompanyShareholderDto[]>(
      `/bff/portfolio/companies/${companyUuid}/shareholders`,
      {
        params: { onlyCurrent: String(onlyCurrent) },
      },
    ),

  listShareholderHistory: (companyUuid: string) =>
    createAppRequest()<ShareholderHistoryVersion[]>(
      `/bff/portfolio/companies/${companyUuid}/shareholders/history`,
    ),

  createShareholder: (companyUuid: string, data: ShareholderInput) =>
    createAppRequest()<CompanyShareholderDto>(
      `/bff/portfolio/companies/${companyUuid}/shareholders`,
      { method: 'POST', data },
    ),

  updateShareholder: (
    companyUuid: string,
    shareholderUuid: string,
    data: Partial<ShareholderInput> & { isCurrent?: boolean },
  ) =>
    createAppRequest()<CompanyShareholderDto>(
      `/bff/portfolio/companies/${companyUuid}/shareholders/${shareholderUuid}`,
      { method: 'PATCH', data },
    ),

  deleteShareholder: (companyUuid: string, shareholderUuid: string) =>
    createAppRequest()<{ uuid: string; deleted: boolean }>(
      `/bff/portfolio/companies/${companyUuid}/shareholders/${shareholderUuid}`,
      { method: 'DELETE' },
    ),

  /** 股权变动：旧行归档为历史名册，按变动后信息新增当前行。 */
  changeShareholder: (
    companyUuid: string,
    shareholderUuid: string,
    data: {
      holdingRatio: number;
      subscribedCapital?: number;
      paidInCapital?: number;
      shareQuantity?: number;
      paidInRatio?: number;
      contributionMethod?: string;
      effectiveDate: string;
      reason?: string;
    },
  ) =>
    createAppRequest()<CompanyShareholderDto>(
      `/bff/portfolio/companies/${companyUuid}/shareholders/${shareholderUuid}/change`,
      { method: 'POST', data },
    ),
};

/** 股东名册行（字段与 inv_portfolio_company_shareholder 一一对应）。 */
export interface CompanyShareholderDto {
  uuid: string;
  tenantUuid?: string;
  companyUuid: string;
  shareholderName: string;
  shareholderType: string;
  /** 持股比例（%）。 */
  holdingRatio: number;
  /** 认缴出资额（万元）。 */
  subscribedCapital: number;
  /** 实缴出资额（万元）。 */
  paidInCapital: number;
  /** 股份数（股）。 */
  shareQuantity: number;
  /** 实缴比例（%）。 */
  paidInRatio: number;
  /** 出资方式。 */
  contributionMethod: string;
  /** 名册基准日 YYYY-MM-DD。 */
  asOfDate: string;
  isCurrent: boolean;
  sourceAttachmentUuid?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 股东名册新增/修正入参。 */
export interface ShareholderInput {
  shareholderName: string;
  shareholderType?: string;
  holdingRatio?: number;
  subscribedCapital?: number;
  paidInCapital?: number;
  shareQuantity?: number;
  paidInRatio?: number;
  contributionMethod?: string;
  asOfDate?: string;
  sourceAttachmentUuid?: string;
}

/** 股权结构每次增删改后的完整版本，旧留痕保留为单行变更。 */
export interface ShareholderHistoryVersion {
  uuid: string;
  version: number;
  updatedAt: string;
  action: string;
  shareholderName: string;
  scope: 'snapshot' | 'change';
  shareholders: Array<Pick<CompanyShareholderDto,
    'uuid' | 'shareholderName' | 'subscribedCapital' | 'holdingRatio'>>;
}
